import { z } from "zod";

const profileSizeBucketLabels: Record<string, number> = {
    toy: 0,
    small: 1,
    medium: 2,
    large: 3,
    giant: 4,
};

export const profileSizeBucketsSchema = z.preprocess((value) => {
    if (!Array.isArray(value)) return value;
    return value.flatMap((item) => {
        if (typeof item === "number") return [item];
        if (typeof item !== "string") return [];
        const bucket = profileSizeBucketLabels[item.trim().toLowerCase()];
        return bucket == null ? [] : [bucket];
    });
}, z.array(z.number()));
export type ProfileSizeBuckets = z.infer<typeof profileSizeBucketsSchema>;

export const profileAgeRangesSchema = z.array(
    z.enum(["2-4_months", "4-6_months", "6_months-2_years", "2+_years"]),
);
export type ProfileAgeRanges = z.infer<typeof profileAgeRangesSchema>;

export const profileSexSchema = z.array(z.enum(["MALE", "FEMALE"]));
export type ProfileSex = z.infer<typeof profileSexSchema>;

export const profileColorsSchema = z.array(z.string());
export type ProfileColors = z.infer<typeof profileColorsSchema>;

export const profileSourcePreferenceSchema = z.array(
    z.enum(["breeder", "shelter"]),
);
export type ProfileSourcePreference = z.infer<
    typeof profileSourcePreferenceSchema
>;

export const profileHousingTypeSchema = z.enum([
    "apartment",
    "condo",
    "townhouse",
    "house",
    "other",
]);
export type ProfileHousingType = z.infer<typeof profileHousingTypeSchema>;

export const profileFenceTypeSchema = z.enum([
    "physical",
    "electric",
    "none",
    "unknown",
]);
export type ProfileFenceType = z.infer<typeof profileFenceTypeSchema>;

export const profileTraitLevelSchema = z.enum(["low", "medium", "high"]);
export type ProfileTraitLevel = z.infer<typeof profileTraitLevelSchema>;

export const profileBreedExperienceSlugsSchema = z.array(z.string());
export type ProfileBreedExperienceSlugs = z.infer<
    typeof profileBreedExperienceSlugsSchema
>;

export const profileTargetSchema = z.object({
    target: z.number(),
    weight: z.number(),
});

export const profileLocationSchema = z.object({
    city: z.string().optional(),
    state: z.string().optional(),
    lat: z.number().optional(),
    lng: z.number().optional(),
    radius: z.number().optional(),
});

export const profileFeeRangeSchema = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
});

export const profileSchema = z.object({
    sizeBuckets: profileSizeBucketsSchema.optional(),
    requireAllergyFriendly: z.boolean().optional(),
    requireKidFriendly: z.boolean().optional(),
    avoidCatUnsafe: z.boolean().optional(),
    avoidDogUnsafe: z.boolean().optional(),
    avoidPreyRisk: z.boolean().optional(),
    avoidHealthRisk: z.boolean().optional(),
    targets: z
        .object({
            energy: profileTargetSchema.optional(),
            social: profileTargetSchema.optional(),
            health: profileTargetSchema.optional(),
        })
        .optional(),
    lowShedding: z.boolean().optional(),
    housingType: profileHousingTypeSchema.optional(),
    ownsHome: z.boolean().optional(),
    hasYard: z.boolean().optional(),
    hasFencedYard: z.boolean().optional(),
    fenceType: profileFenceTypeSchema.optional(),
    landlordAllowsPets: z.boolean().optional(),
    landlordApprovalAvailable: z.boolean().optional(),
    adultOnlyHousehold: z.boolean().optional(),
    youngestChildAge: z.number().optional(),
    hasFrequentChildVisitors: z.boolean().optional(),
    hasElderlyOrFragileResidents: z.boolean().optional(),
    lowAloneTolerance: z.boolean().optional(),
    lowTrainabilityTolerance: z.boolean().optional(),
    hasKids: z.boolean().optional(),
    hasOtherPets: z.boolean().optional(),
    hasDogNumber: z.number().optional(),
    hasCatNumber: z.number().optional(),
    otherDogCount: z.number().optional(),
    catCount: z.number().optional(),
    hasSmallAnimals: z.boolean().optional(),
    hasDogExperience: z.boolean().optional(),
    firstTimeDogOwner: z.boolean().optional(),
    hasLargeDogExperience: z.boolean().optional(),
    breedExperienceSlugs: profileBreedExperienceSlugsSchema.optional(),
    hasRescueDogExperience: z.boolean().optional(),
    trainingCommitment: profileTraitLevelSchema.optional(),
    location: profileLocationSchema.optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    feeRange: profileFeeRangeSchema.optional(),
    canTravelForAdoption: z.boolean().optional(),
    maxTravelDistanceMiles: z.number().optional(),
    willingToDoHomeVisit: z.boolean().optional(),
    willingToProvideVetReference: z.boolean().optional(),
    willingToProvidePersonalReferences: z.boolean().optional(),
    willingToSignAdoptionContract: z.boolean().optional(),
    willingToAllowFollowUp: z.boolean().optional(),
    willingToDoMeetAndGreet: z.boolean().optional(),
    hoursAloneDaily: z.number().optional(),
    workFromHome: z.boolean().optional(),
    activityLevel: profileTraitLevelSchema.optional(),
    exerciseMinutesDaily: z.number().optional(),
    groomingTolerance: profileTraitLevelSchema.optional(),
    medicalCareBudgetLevel: profileTraitLevelSchema.optional(),
    acceptsSpecialNeeds: z.boolean().optional(),
    acceptsBehavioralNeeds: z.boolean().optional(),
    acceptsSeniorDog: z.boolean().optional(),
    acceptsPuppy: z.boolean().optional(),
    acceptsRequiredSpayNeuter: z.boolean().optional(),
    acceptsBreederWaitlist: z.boolean().optional(),
    age18Plus: z.boolean().optional(),
    agePreference: z.enum(["puppy", "adult", "either"]).optional(),
    ageRanges: profileAgeRangesSchema.optional(),
    sex: profileSexSchema.optional(),
    colors: profileColorsSchema.optional(),
    sourcePreference: profileSourcePreferenceSchema.optional(),
    timelineWeeks: z.number().optional(),
    onboardingCompletedAt: z.string().optional(),
    updatedAt: z.string().optional(),
    provenance: z.record(z.string(), z.unknown()).optional(),
});
export type Profile = z.infer<typeof profileSchema>;

export const profileTraitsSchema = profileSchema.omit({
    onboardingCompletedAt: true,
    provenance: true,
    updatedAt: true,
});
export type ProfileTraits = z.infer<typeof profileTraitsSchema>;

export const profileComparableTraitsSchema = profileTraitsSchema.omit({
    feeRange: true,
    location: true,
    targets: true,
});
export type ProfileComparableTraits = z.infer<
    typeof profileComparableTraitsSchema
>;

export const viewerSizeBucketsSchema = profileSizeBucketsSchema;
export type ViewerSizeBuckets = ProfileSizeBuckets;
export const viewerAgeRangesSchema = profileAgeRangesSchema;
export type ViewerAgeRanges = ProfileAgeRanges;
export const viewerSexSchema = profileSexSchema;
export type ViewerSex = ProfileSex;
export const viewerColorsSchema = profileColorsSchema;
export type ViewerColors = ProfileColors;
export const viewerSourcePreferenceSchema = profileSourcePreferenceSchema;
export type ViewerSourcePreference = ProfileSourcePreference;
export const viewerHousingTypeSchema = profileHousingTypeSchema;
export type ViewerHousingType = ProfileHousingType;
export const viewerFenceTypeSchema = profileFenceTypeSchema;
export type ViewerFenceType = ProfileFenceType;
export const viewerTraitLevelSchema = profileTraitLevelSchema;
export type ViewerTraitLevel = ProfileTraitLevel;
export const viewerBreedExperienceSlugsSchema =
    profileBreedExperienceSlugsSchema;
export type ViewerBreedExperienceSlugs = ProfileBreedExperienceSlugs;
export const viewerProfileSchema = profileSchema;
export type ViewerProfile = Profile;
